import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import type { FormFieldData } from './FormBuilder';

interface FormPreviewProps {
  title?: string;
  description?: string;
  fields: FormFieldData[];
  submitLabel?: string;
}

const WIDTH_CLASS: Record<NonNullable<FormFieldData['width']>, string> = {
  full: 'col-span-12',
  'two-thirds': 'col-span-12 md:col-span-8',
  half: 'col-span-12 md:col-span-6',
  third: 'col-span-12 md:col-span-4',
};

export function FormPreview({ title, description, fields, submitLabel = 'Enviar' }: FormPreviewProps) {
  return (
    <Card className="p-6">
      <div className="mb-4">
        {title && <h3 className="text-lg font-semibold">{title}</h3>}
        {description && <p className="text-sm text-neutral-500">{description}</p>}
      </div>
      <form
        className="grid grid-cols-12 gap-3"
        onSubmit={(e) => { e.preventDefault(); }}
      >
        {fields.map((f) => (
          <div key={f.id} className={cn(WIDTH_CLASS[f.width ?? 'full'], 'space-y-1.5')}>
            {f.type !== 'hidden' && f.type !== 'checkbox' && (
              <Label>
                {f.label} {f.required && <span className="text-red-500">*</span>}
              </Label>
            )}
            {renderInput(f)}
            {f.helper && <p className="text-xs text-neutral-500">{f.helper}</p>}
          </div>
        ))}
        <div className="col-span-12">
          <Button type="submit">{submitLabel}</Button>
        </div>
      </form>
    </Card>
  );
}

function renderInput(f: FormFieldData) {
  switch (f.type) {
    case 'textarea':
      return <Textarea placeholder={f.placeholder} required={f.required} />;
    case 'select':
      return (
        <select
          required={f.required}
          className="h-9 w-full rounded-md border border-neutral-200 bg-white px-2 text-sm"
          defaultValue=""
        >
          <option value="" disabled>{f.placeholder || '—'}</option>
          {(f.options ?? []).map((o, i) => (
            <option key={i} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    case 'radio':
      return (
        <div className="space-y-1">
          {(f.options ?? []).map((o, i) => (
            <label key={i} className="flex items-center gap-2 text-sm">
              <input type="radio" name={f.name} value={o.value} className="accent-neutral-900" /> {o.label}
            </label>
          ))}
        </div>
      );
    case 'checkbox':
      return (
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" required={f.required} className="mt-0.5 accent-neutral-900" />
          <span>{f.label} {f.required && <span className="text-red-500">*</span>}</span>
        </label>
      );
    case 'file':
      return <Input type="file" required={f.required} />;
    case 'date':
      return <Input type="date" required={f.required} />;
    case 'number':
      return <Input type="number" required={f.required} placeholder={f.placeholder} />;
    case 'email':
      return <Input type="email" required={f.required} placeholder={f.placeholder} />;
    case 'tel':
      return <Input type="tel" required={f.required} placeholder={f.placeholder} />;
    case 'url':
      return <Input type="url" required={f.required} placeholder={f.placeholder} />;
    case 'hidden':
      return <input type="hidden" name={f.name} />;
    default:
      return <Input type="text" required={f.required} placeholder={f.placeholder} />;
  }
}
